import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
  colSpan: string;
  rowSpan: string;
}

export default function ServicesBentoGrid() {
  const services: Service[] = [
    {
      id: 'social_media',
      title: 'Social Media Marketing',
      description: 'Comprehensive social media strategies for restaurants, hospitals, hotels, and more.',
      icon: 'ChatBubbleBottomCenterTextIcon',
      colSpan: 'col-span-2',
      rowSpan: 'row-span-1'
    },
    {
      id: 'web_dev',
      title: 'Website Development',
      description: 'Modern, responsive websites that convert visitors into customers.',
      icon: 'GlobeAltIcon',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      id: 'app_dev',
      title: 'App Development',
      description: 'Custom mobile applications for iOS and Android platforms.',
      icon: 'DevicePhoneMobileIcon',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      id: 'graphic_design',
      title: 'Graphic Designing',
      description: 'Eye-catching visuals that elevate your brand identity.',
      icon: 'SparklesIcon',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      id: 'print_solutions',
      title: 'Print Solutions',
      description: 'Professional printing services for all your marketing materials.',
      icon: 'PrinterIcon',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      id: 'product_shoots',
      title: 'Product Shoots',
      description: 'Professional photography that showcases your products beautifully.',
      icon: 'CameraIcon',
      colSpan: 'col-span-2',
      rowSpan: 'row-span-1'
    },
    {
      id: 'logo_design',
      title: 'Logo Designing',
      description: 'Unique and memorable logos that define your brand.',
      icon: 'PaintBrushIcon',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      id: 'whatsapp_marketing',
      title: 'WhatsApp Bulk Marketing',
      description: 'Reach thousands of customers instantly with targeted WhatsApp campaigns.',
      icon: 'ChatBubbleLeftRightIcon',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      id: 'voice_blast',
      title: 'Voice Blast Marketing',
      description: 'Automated voice call campaigns for effective customer outreach.',
      icon: 'PhoneIcon',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      id: 'ivr_solutions',
      title: 'IVR Solutions',
      description: 'Interactive voice response systems for seamless customer service.',
      icon: 'PhoneArrowDownLeftIcon',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      id: 'chatbot_ai',
      title: 'Chatbot & AI Agents',
      description: 'Intelligent chatbots and AI-powered customer support solutions.',
      icon: 'CpuChipIcon',
      colSpan: 'col-span-2',
      rowSpan: 'row-span-1'
    },
    {
      id: 'festival_greetings',
      title: 'Festival Greetings',
      description: 'Year-long festival greeting plans for consistent brand presence.',
      icon: 'GiftIcon',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      id: 'wedding_invites',
      title: 'Wedding Invites',
      description: 'Beautiful, personalized wedding invitation designs.',
      icon: 'HeartIcon',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      id: 'ecatalogue',
      title: 'E-Catalogue & Brochures',
      description: 'Digital catalogues and brochures for product showcases.',
      icon: 'DocumentTextIcon',
      colSpan: 'col-span-2',
      rowSpan: 'row-span-1'
    }
  ];


  return (
    <section className="py-20 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 reveal">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-[2px] h-4 bg-amber-500" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-400">
              Our Services
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
            Everything You Need,<br />One Platform
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            From social media strategies to complete digital solutions, we've got you covered.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[240px]">
          {services.map((service, idx) =>
          <div
            key={service.id}
            className={`${service.colSpan} ${service.rowSpan} bg-zinc-900 border border-zinc-800 rounded-2xl p-8 flex flex-col justify-between overflow-hidden relative group hover:border-amber-500/20 transition-all duration-500 glow-hover reveal hover-lift`}
            style={{ transitionDelay: `${idx * 50}ms` }}>
            
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative z-10">
                <div className="w-12 h-12 mb-4 flex items-center justify-center text-amber-500 bg-amber-500/10 rounded-xl group-hover:bg-amber-500 group-hover:text-white transition-all">
                  <Icon name={service.icon as any} size={24} />
                </div>
                <h3 className="text-xl font-bold mb-2 text-white group-hover:text-amber-500 transition-colors">
                  {service.title}
                </h3>
              </div>

              <p className="text-sm text-zinc-400 leading-relaxed relative z-10">
                {service.description}
              </p>

              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl group-hover:bg-amber-500/20 transition-all" />
            </div>
          )}
        </div>

        {/* Service Images Showcase */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 reveal">
          {[
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_14c406c85-1764746937480.png", alt: 'Social media marketing content creation with engaging graphics and posts', title: 'Social Media' },
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_11a0236bb-1764651527759.png", alt: 'Modern responsive website development on multiple devices', title: 'Web Development' },
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1a709d781-1766577185684.png", alt: 'Mobile app development and user interface design process', title: 'App Development' },
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_142f0fd7e-1764992112140.png", alt: 'Creative graphic design work with color palettes and typography', title: 'Graphic Design' }]?.
          map((img, idx) =>
          <div
            key={idx}
            className="relative aspect-[4/3] rounded-2xl overflow-hidden group hover-lift"
            style={{ animationDelay: `${idx * 100}ms` }}>
            
              <AppImage
              src={img?.src}
              alt={img?.alt}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white font-bold text-lg">{img?.title}</div>
            </div>
          )}
        </div>
      </div>
    </section>);

}